import React, { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Stack,
  SwipeableDrawer,
  Typography,
  useTheme,
} from "@mui/material";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { getReportDetail } from "../../../apis";
import { detailAdvisorReview } from "../../../apis/apiAdvisorReview";
import { useSelector } from "react-redux";
import { IconHeader } from "../../Icon/IconHader/IconHeader";
import { AdvisorReview } from "../All/Information/AdvisorReview";
import { Complements } from "../All/Information/Complements";
import { parseDateToLgComun4 } from "../../../helpers/dateTransform";
import { Report } from "../../Loading/Report/Report";
import { useReportContext } from "../ReportContext/useReportContext";
import { useNavigate } from "react-router";
import Comments from "../All/Information/Comments";
import EGAccordion from "../../MainComponents/Accordion/EGAccordion/EGAccordion";
import { IndicatorsList } from "../../MainComponents/Indicators/IndicatorsList";

export const ReportGeneralInfo = ({
  setShowReportSummary,
  showReportSummary,
  selectedRow,
  toggleDrawer,
  handleClose,
  transformedType,
  getLocalizedLabel,
  icon_indicators,
  translation,
}) => {
  // Custom hooks
  const theme = useTheme();
  const [t] = useTranslation("information");
  const { enqueueSnackbar } = useSnackbar();
  const { lang } = useSelector((state) => state.lang);
  const { involvedColor } = useReportContext();
  const navigate = useNavigate();
  // States
  const [isLoading, setIsLoading] = useState(true);
  const [isCopy, setIsCopy] = useState(false);
  const [reportDetail, setReportDetail] = useState({});
  const [isShown, setIsShown] = useState(false);

  // Get Report detail
  useEffect(() => {
    setIsLoading(true);
    const fetchReportDetail = async () => {
      if (!selectedRow) return;
      try {
        const data = await getReportDetail(selectedRow.id);
        setReportDetail(data);
      } catch (error) {
        console.error("Error fetching report details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportDetail();
  }, [selectedRow]);

  // Check if the report has an advisor review to conditionally show the panel
  useEffect(() => {
    const checkAdvisorReview = async () => {
      try {
        const response = await detailAdvisorReview(reportDetail.id);
        if (response && Object.keys(response).length > 0) {
          setIsShown(true);
        }
      } catch (error) {}
    };

    if (reportDetail?.id) {
      checkAdvisorReview();
    }
  }, [reportDetail]);

  useEffect(() => {
    if (!showReportSummary) {
      setReportDetail({});
      setIsShown(false);
    }
  }, [showReportSummary]);

  // Copy text to clipboard
  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopy(true);
      enqueueSnackbar(t("information.copied_to_clipboard"), {
        variant: "info",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      setTimeout(() => {
        setIsCopy(false);
      }, 2000);
    } catch (error) {
      console.error("could not copy");
    }
  };

  const involvedOrder = {
    victim: 1,
    witness: 2,
    offender: 3,
    accomplice: 4,
  };

  const CapturedInformation = () => {
    ////////// Obtaining report form label //////////
    const getFormLabel = (label, lang, default_value = "") => {
      if (typeof label === "string") {
        return label;
      } else {
        if (lang in label) {
          return label[lang];
        } else {
          return default_value;
        }
      }
    };

    return (
      <Stack spacing={2} paddingX={0.8} paddingY={1}>
        {reportDetail?.stepers?.map((item, index) => (
          // Section container
          <Box key={index}>
            {/* Section title */}
            <Box marginBottom={1.5}>
              <IconHeader
                variant="span"
                key={index}
                iconName={
                  index === 0
                    ? "personal_id"
                    : index === 1
                    ? "outline_location"
                    : index === 2
                    ? "outline_note_search"
                    : "outline_direct_normal"
                }
                title={
                  item.locales ? getFormLabel(item.locales, lang) : item.title
                }
              />
            </Box>
            {/* Section Content */}
            <Stack
              display="flex"
              alignItems="center"
              flexDirection="row"
              flexWrap="wrap"
              padding={0}
              rowGap={1.5}
              columnGap={2.0}
            >
              {item?.form?.map((form, idx) => {
                let is_array = false;

                if (form.key === "subject" || form.key === "description") {
                  return <div key={idx}></div>;
                } else {
                  let the_label = form.label;

                  if (form.locale != undefined) {
                    the_label = form.locale;
                  }

                  let show_value = form.text_value;

                  if (form.locales_value != null) {
                    show_value = getFormLabel(
                      form.locales_value,
                      lang,
                      show_value
                    );
                  }

                  if (Array.isArray(show_value)) {
                    show_value = show_value
                      .sort(
                        (a, b) => involvedOrder[a.type] - involvedOrder[b.type]
                      )
                      .map((item, index) => (
                        <Typography
                          component="span"
                          fontSize={12}
                          key={index}
                          sx={{
                            width: {
                              xs: "100%",
                              sm: show_value.length > 1 ? "49%" : "100%",
                            },
                            border: `1px solid ${involvedColor[item.type]}`,
                            backgroundColor: alpha(
                              involvedColor[item.type],
                              0.1
                            ),
                            px: 1,
                            py: 0.5,
                          }}
                          color={involvedColor[item.type]}
                          className="rounded"
                        >
                          <b>
                            {t(`information.involved_${item.type}`)}: &nbsp;
                          </b>
                          {`${item.name} (${item?.occupation})`}
                        </Typography>
                      ));

                    is_array = true;
                  }

                  if (typeof show_value === "boolean") {
                    return null;
                  }

                  return (
                    <Stack key={idx}>
                      {/* Involved list */}
                      {is_array ? (
                        <Stack direction={"column"} m={1}>
                          <Typography
                            variant="div"
                            color="secondary"
                            fontSize={12}
                            fontWeight={600}
                          >
                            {`${t("information.report_involveds")}:`}
                          </Typography>
                          <Stack
                            spacing={1}
                            sx={{
                              alignItems: "stretch",
                              justifyContent: "space-around",
                              flexFlow: "row wrap",
                            }}
                          >
                            {show_value}
                          </Stack>
                        </Stack>
                      ) : (
                        // Form
                        <Stack key={idx}>
                          {/* Question */}
                          <Typography
                            fontWeight={600}
                            component="div"
                            color="secondary"
                            fontSize={12}
                          >
                            {getFormLabel(the_label, lang)}&nbsp;
                          </Typography>
                          {/* Answer */}
                          <Typography
                            variant="subtitle2"
                            component="div"
                            color={theme.palette.oxfordBlue.main}
                            borderBottom={`1px solid ${theme.palette.blueGray100.main}`}
                            fontSize={13}
                          >
                            {show_value ? String(show_value) : "-----"}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  );
                }
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const panels = [
    {
      id: "panel1",
      title: t("information.captured_information"),
      titleBackgrundColor: theme.palette.primary.main,
      titleColor: theme.palette.primary.contrastText,
      contentBorderColor: theme.palette.primary.main,
      content: <CapturedInformation />,
    },
    ...(isShown
      ? [
          {
            id: "panel2",
            title: t("AdvisorReview.advisor_review"),
            titleBackgrundColor: theme.palette.secondary.main,
            titleColor: theme.palette.secondary.contrastText,
            contentBorderColor: theme.palette.secondary.main,
            content: <AdvisorReview report_id={reportDetail.id} />,
          },
        ]
      : []),
    ...((reportDetail?.complements?.length > 0 && [
      {
        id: "panel3",
        title: `${t("information.report_complements")}`,
        titleBackgrundColor: theme.palette.blueGray100.main,
        titleColor: theme.palette.blueGray100.contrastText,
        contentBorderColor: theme.palette.blueGray100.main,
        content: <Complements complements={reportDetail?.complements} />,
      },
    ]) ||
      []),
  ];

  return (
    <SwipeableDrawer
      anchor="right"
      open={showReportSummary}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          zIndex: (theme) => theme.zIndex.drawer + 2,
          borderRadius: "15px 0 0 15px",
          width: { xs: "100vw", md: "60vw" },
        },
      }}
    >
      {/* Title */}
      <Box
        sx={{
          padding: 2,
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
        bgcolor={theme.palette.primary.main}
      >
        <EGIconButton
          iconName="close_icon"
          onClick={toggleDrawer(false)}
          iconColor="white"
          colorHover="secondary"
          iconSize={20}
          sx={{
            position: "absolute",
            top: 20,
            left: 20,
            zIndex: 1,
          }}
        />

        <Typography
          textAlign="center"
          variant="h5"
          color={theme.palette.background.paper}
          sx={{
            mb: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {`${t("information.report_summary")}: ${selectedRow?.folio}`}
          <EGIconButton
            iconName="bold_document_forward"
            iconColor="white"
            sx={{ mx: 0.5 }}
            iconSize={19}
            title={t("information.view_report")}
            onClick={() => {
              toggleDrawer(false);
              setShowReportSummary(false);
              navigate(`/reports/${selectedRow?.id}`);
              handleClose?.();
            }}
          />
        </Typography>

        {/* tracking_code hidden */}
      </Box>
      {/* Report summary */}
      <Box>
        {isLoading ? (
          <Report />
        ) : (
          <div className="p-20">
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                flexFlow: "row wrap",
              }}
            >
              <Typography
                variant="body2"
                color="blueGray600.main"
                component="div"
                fontSize={12}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  {`${t("information.generated_report")}: ${
                    parseDateToLgComun4(
                      reportDetail?.created_at,
                      t("information.locale")
                    ) || t("information.no_updates")
                  }`}
                </Box>
                <Box>{t("information.recived_by")}</Box>
              </Typography>

              <Box>
                <IndicatorsList
                  indicators={reportDetail.indicators}
                  entryway={reportDetail.entry_way}
                  tags={reportDetail.tags}
                  transformedType={transformedType}
                  getLocalizedLabel={getLocalizedLabel}
                  icon_indicators={icon_indicators}
                  t={translation}
                  theme={theme}
                />
              </Box>
            </Stack>
            {/* Main information */}
            <Box
              className="investigation__header rounded dyTheme2"
              mt={1.7}
              sx={{
                border: `1px solid ${theme.palette.primary.main}`,
                px: 1.5,
                py: 1,
              }}
            >
              <Typography
                variant="h5"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                {reportDetail?.subject || t("information.no_subject")}
              </Typography>

              <Typography variant="h6" color="primary" mt={1} fontSize={9}>
                {`${t("information.report_description")}:`}
              </Typography>

              <Comments
                padding={0.3}
                maxLines={10}
                maxLength={500}
                fontSize={13}
                text={
                  reportDetail?.description || t("information.no_description")
                }
                color="secondary"
                rounded
              />
            </Box>

            <div className="mt-20">
              <EGAccordion panels={panels} defaultExpanded="panel1" />
            </div>
          </div>
        )}
      </Box>
    </SwipeableDrawer>
  );
};
